import { z } from 'zod'

import { getTranslate } from '@/legacy-src/utilities/i18n'

import { FieldType, type FieldConfig } from './types'

export interface ValidationMessages {
  required: string
  emailBadFormat: string
  phoneBadFormat: string
  dataProtectionNotChecked: string
}

/**
 * Creates a Zod schema for a single field based on its configuration
 */
export const createFieldSchema = (field: FieldConfig, messages: ValidationMessages): z.ZodTypeAny | null => {
  // Skip only submit buttons - all other fields can have validation
  if (field.fieldType === FieldType.SUBMIT) {
    return null
  }

  let fieldSchema: z.ZodTypeAny

  switch (field.fieldType) {
    case FieldType.BUTTONS_CHECKBOX:
      // Multi-select field - array of selected values
      if (field.required) {
        fieldSchema = z.array(z.string()).min(1, {
          message: field.customValidationMessage || messages.required,
        })
      } else {
        fieldSchema = z.array(z.string()).optional()
      }
      break

    case FieldType.BUTTONS_RADIO:
    case FieldType.RADIO:
      // Single select field
      if (field.required) {
        fieldSchema = z.string({ message: messages.required }).min(1, {
          message: field.customValidationMessage || messages.required,
        })
      } else {
        fieldSchema = z.string().optional()
      }
      break

    case FieldType.CHECKBOX:
      // Multi-select checkbox field - array of selected values
      if (field.required) {
        fieldSchema = z.array(z.string()).min(1, {
          message: field.customValidationMessage || messages.required,
        })
      } else {
        fieldSchema = z.array(z.string()).optional()
      }
      break

    case FieldType.TEXT:
      if (field.required) {
        const minLength = field.minLength || 2
        fieldSchema = z.string({ message: messages.required }).min(minLength, {
          message: field.customValidationMessage || messages.required,
        })
      } else {
        fieldSchema = z.string().optional()
      }
      break

    case FieldType.EMAIL:
      if (field.required) {
        fieldSchema = z.string({ message: messages.required }).email({
          message: field.customValidationMessage || messages.emailBadFormat,
        })
      } else {
        fieldSchema = z
          .string()
          .email({
            message: messages.emailBadFormat,
          })
          .nullish()
      }
      break

    case FieldType.TEL:
      if (field.required) {
        const minLength = field.minLength || 9
        fieldSchema = z.string({ message: messages.required }).min(minLength, {
          message: field.customValidationMessage || messages.phoneBadFormat,
        })
      } else {
        fieldSchema = z.string().optional()
      }
      break

    case FieldType.TEXTAREA:
      if (field.required) {
        const minLength = field.minLength || 1
        fieldSchema = z.string({ message: messages.required }).min(minLength, {
          message: field.customValidationMessage || messages.required,
        })
      } else {
        fieldSchema = z.string().optional()
      }
      break

    case FieldType.SELECT:
      if (field.required) {
        fieldSchema = z.string({ message: messages.required }).min(1, {
          message: field.customValidationMessage || messages.required,
        })
      } else {
        fieldSchema = z.string().optional()
      }
      break

    case FieldType.CONSENT:
      if (field.required) {
        fieldSchema = z.boolean({ message: messages.dataProtectionNotChecked }).refine((val) => val === true, {
          message: field.customValidationMessage || messages.dataProtectionNotChecked,
        })
      } else {
        fieldSchema = z.boolean().optional()
      }
      break

    case FieldType.NUMBER:
      if (field.required) {
        fieldSchema = z.number({
          message: field.customValidationMessage || messages.required,
        })
      } else {
        fieldSchema = z.number().optional()
      }
      break

    case FieldType.PASSWORD:
      if (field.required) {
        const minLength = field.minLength || 6
        fieldSchema = z.string({ message: messages.required }).min(minLength, {
          message: field.customValidationMessage || messages.required,
        })
      } else {
        fieldSchema = z.string().optional()
      }
      break

    default:
      // For unknown field types, apply basic string validation if required
      if (field.required) {
        fieldSchema = z.string().min(1, {
          message: field.customValidationMessage || messages.required,
        })
      } else {
        fieldSchema = z.string().optional()
      }
  }

  return fieldSchema
}

/**
 * Creates default validation messages
 */
const getDefaultValidationMessages = (): ValidationMessages => {
  const { t } = getTranslate()

  return {
    required: t('validationErrors.required'),
    emailBadFormat: t('validationErrors.email.badEmailFormat'),
    phoneBadFormat: t('validationErrors.badPhoneFormat'),
    dataProtectionNotChecked: t('validationErrors.dataProtection.notChecked'),
  }
}

/**
 * Creates a complete Zod schema for form sections
 */
export const createFormSchema = (
  sections: Array<{ fields: FieldConfig[] }>,
  messages?: Partial<ValidationMessages>,
) => {
  const defaultMessages = getDefaultValidationMessages()
  const finalMessages = { ...defaultMessages, ...messages }
  const schema: Record<string, z.ZodTypeAny> = {}

  sections.forEach((section) => {
    section.fields.forEach((field) => {
      const fieldSchema = createFieldSchema(field, finalMessages)
      if (fieldSchema) {
        schema[field.id] = fieldSchema
      }
    })
  })

  return z.object(schema)
}
